/**
 * # Registration receipts — what a plugin registration actually claimed
 *
 * `RenderPluginManager.register` writes one plugin into its map under SEVERAL
 * keys: the canonical `(id, role)` plus every alias the shared catalog attaches
 * to it (`RenderPluginSpec.lookupIds`). Until this module that expansion was
 * invisible — registration returned `this`, and the only way to answer "which
 * keys does the DOM answer?" was a second, hand-maintained manifest of the same
 * triples (`plugin-keys.ts`: "update this list when a DOM plugin's identity
 * changes"). Two manifests of one fact drift, and this one had: the hand list
 * carried no aliases for `timeline[row]` and no entry for `alias.chart` at all,
 * and its parity test had been red on both counts.
 *
 * A **receipt** is the registration telling you what it took:
 *
 *   - `keys` — every role-qualified key this registration answers.
 *   - `aliases` — each alias key mapped to the canonical key it resolves to.
 *
 * Receipts merge, so a manager accumulates one cumulative receipt across every
 * `register` call, and parity compares THAT to the catalog instead of comparing
 * a hand list to it. There is no second manifest to keep in step: the receipt is
 * computed by the same function registration used.
 *
 * Pure and DOM-free by construction — it stops at ids, roles and aliases — so
 * the parity test loads it under plain Deno even though `render-plugin.ts`
 * (which registers concrete `HTMLElement`-bearing plugins) cannot be loaded
 * there at all.
 */

import { type Role } from "@tento-lona/sheets";
import { RenderPluginSpec } from "@tento-lona/sheets/platform";
import { deepFrozen, frozenMap, frozenSet } from "@tento-lona/sheets/internal-ui";

/**
 * A role-qualified registration key, `${role}:${id}` — the exact string
 * `RenderPluginManager` keys its plugin map by (`RenderPluginSpec.mapKey`).
 *
 * Branded so a key cannot be confused with a bare plugin id: `"column.text"`
 * and `"renderer:column.text"` are both strings and only one of them is a
 * registration key. Minted only by {@link RenderPluginKey.of} or by reading an
 * existing receipt.
 */
export type RenderPluginKey = string & { readonly __brand: "RenderPluginKey" };

/** Values that go with `RenderPluginKey`: `of`. */
export namespace RenderPluginKey {
  /** The only constructor: role-qualify an id the way registration does. */
  export function of(id: string, role: Role): RenderPluginKey {
    return RenderPluginSpec.mapKey(id, role) as RenderPluginKey;
  }
}

/**
 * What one registration (or an accumulation of them) claimed.
 *
 * `keys` is the whole answered surface; `aliases` is the subset that is an
 * alias, pointing at the canonical key it stands for. Canonical keys are
 * therefore `keys` minus `aliases.keys()` — see {@link
 * RegistrationReceipt.canonicalKeys}.
 */
export interface RegistrationReceipt {
  readonly keys: ReadonlySet<RenderPluginKey>;
  readonly aliases: ReadonlyMap<RenderPluginKey, RenderPluginKey>;
}

/** Why a lookup found nothing registered. */
export type RegistrationMissReason =
  /** No registration answers this id under ANY role. */
  | "unregistered"
  /** The id is registered — under a different role than the one asked for. */
  | "role-mismatch";

/** The outcome of resolving one `(id, role)` against a receipt. */
export type RegistrationLookup =
  | {
    readonly kind: "registered";
    /** The key that matched — the alias when resolution went through one. */
    readonly key: RenderPluginKey;
    /** The registration's own `(id, role)` key. */
    readonly canonical: RenderPluginKey;
  }
  | {
    readonly kind: "missing";
    readonly requested: RenderPluginKey;
    readonly reason: RegistrationMissReason;
    /** Roles this id IS registered under; empty when `"unregistered"`. */
    readonly registeredRoles: readonly Role[];
  };

/**
 * Every `Role`, pinned exhaustive by the record's key set: adding a role to the
 * union stops this object compiling until it is listed here too.
 */
const ROLES_EXHAUSTIVE: Readonly<Record<Role, Role>> = {
  source: "source",
  layout: "layout",
  renderer: "renderer",
  alias: "alias",
  inert: "inert",
};

const ALL_ROLES: readonly Role[] = Object.values(ROLES_EXHAUSTIVE);

const EMPTY_RECEIPT: RegistrationReceipt = deepFrozen({
  keys: frozenSet<RenderPluginKey>([]),
  aliases: frozenMap<RenderPluginKey, RenderPluginKey>([]),
});

/** Identity data a registration reads off a plugin — nothing DOM-shaped. */
export type RegistrationIdentity = Pick<
  RenderPluginSpec,
  "id" | "role" | "aliases"
>;

/**
 * The receipt one registration produces.
 *
 * Key expansion is delegated to `RenderPluginSpec.lookupIds`, the same function
 * `RenderPluginManager.register` walks to fill its map, so a receipt can never
 * describe a registration that did something else. The FIRST id it yields is
 * the spec's own; everything after it is an alias.
 */
function receiptForSpec(spec: RegistrationIdentity): RegistrationReceipt {
  const canonical = RenderPluginKey.of(spec.id, spec.role);
  const keys = new Set<RenderPluginKey>();
  const aliases = new Map<RenderPluginKey, RenderPluginKey>();
  for (const id of RenderPluginSpec.lookupIds(spec)) {
    const key = RenderPluginKey.of(id, spec.role);
    keys.add(key);
    if (key !== canonical) aliases.set(key, canonical);
  }
  return { keys, aliases };
}

/**
 * Accumulate two receipts.
 *
 * Later registrations WIN on a contested key, mirroring the plugin map's
 * `set` — merging must not change what registration does, only describe it.
 * {@link registrationConflicts} is where a contested key becomes visible.
 */
function mergeReceipts(
  base: RegistrationReceipt,
  next: RegistrationReceipt,
): RegistrationReceipt {
  const keys = new Set<RenderPluginKey>(base.keys);
  const aliases = new Map<RenderPluginKey, RenderPluginKey>(base.aliases);
  for (const key of next.keys) keys.add(key);
  for (const [alias, canonical] of next.aliases) aliases.set(alias, canonical);
  // A key that used to be an alias and is now claimed canonically stops being
  // an alias — otherwise `canonicalKeys` would drop a real registration.
  for (const key of next.keys) {
    if (!next.aliases.has(key)) aliases.delete(key);
  }
  return { keys, aliases };
}

/** The cumulative receipt for a whole registration set, in order. */
function receiptOf(
  specs: Iterable<RegistrationIdentity>,
): RegistrationReceipt {
  let receipt = EMPTY_RECEIPT;
  for (const spec of specs) {
    receipt = mergeReceipts(receipt, receiptForSpec(spec));
  }
  return receipt;
}

/** The keys a registration claimed AS ITS OWN — aliases excluded. */
function canonicalKeysOf(
  receipt: RegistrationReceipt,
): ReadonlySet<RenderPluginKey> {
  const canonical = new Set<RenderPluginKey>();
  for (const key of receipt.keys) {
    if (!receipt.aliases.has(key)) canonical.add(key);
  }
  return canonical;
}

/**
 * Resolve one `(id, role)` against a receipt, NAMING the miss.
 *
 * The manager's own lookup answers a miss with the fallback plugin and no
 * explanation, which is how a plugin that was never registered and a plugin
 * registered under the wrong role read identically at the call site. This
 * separates them: `"role-mismatch"` carries the roles the id IS registered
 * under, which is the whole diagnosis.
 */
function resolveInReceipt(
  receipt: RegistrationReceipt,
  id: string,
  role: Role,
): RegistrationLookup {
  const requested = RenderPluginKey.of(id, role);
  const normalized = RenderPluginKey.of(RenderPluginSpec.normalizeId(id), role);
  for (const key of [requested, normalized]) {
    if (!receipt.keys.has(key)) continue;
    return {
      kind: "registered",
      key,
      canonical: receipt.aliases.get(key) ?? key,
    };
  }
  const registeredRoles: Role[] = [];
  for (const other of ALL_ROLES) {
    if (other === role) continue;
    if (receipt.keys.has(RenderPluginKey.of(id, other))) {
      registeredRoles.push(other);
    }
  }
  return {
    kind: "missing",
    requested,
    reason: registeredRoles.length > 0 ? "role-mismatch" : "unregistered",
    registeredRoles,
  };
}

/** One key claimed canonically by two different registrations. */
export interface RegistrationConflict {
  readonly key: RenderPluginKey;
  /** The registration that lost the key, in registration order. */
  readonly displaced: RegistrationIdentity;
  /** The registration that now answers it. */
  readonly winner: RegistrationIdentity;
}

/**
 * Canonical keys claimed twice across a registration set.
 *
 * Registration is last-write-wins and says nothing when it overwrites, so two
 * plugins claiming `renderer:column.text` — or one plugin claiming another's id
 * as an alias — silently retires the first. This reports it as data; the parity
 * test asserts the list is empty.
 */
function conflictsIn(
  specs: Iterable<RegistrationIdentity>,
): readonly RegistrationConflict[] {
  const owner = new Map<RenderPluginKey, RegistrationIdentity>();
  const conflicts: RegistrationConflict[] = [];
  for (const spec of specs) {
    for (const key of receiptForSpec(spec).keys) {
      const previous = owner.get(key);
      if (previous != null) {
        conflicts.push({ key, displaced: previous, winner: spec });
      }
      owner.set(key, spec);
    }
  }
  return conflicts;
}

/**
 * Values that go with `RegistrationReceipt`: `EMPTY`, `forSpec`, `merge`, `of`,
 * `canonicalKeys`, `resolve`, and 1 more.
 */
export namespace RegistrationReceipt {
  /** A receipt claiming nothing — the identity for {@link merge}. */
  export const EMPTY = EMPTY_RECEIPT;
  export const forSpec = receiptForSpec;
  export const merge = mergeReceipts;
  export const of = receiptOf;
  export const canonicalKeys = canonicalKeysOf;
  export const resolve = resolveInReceipt;
  export const conflicts = conflictsIn;
}
